import { useState, useCallback } from 'react';

/**
 * Custom hook for form handling
 * 
 * @param {Object} initialValues - Initial form values
 * @param {Function} onSubmit - Function to call on form submission
 * @param {Function} [validate] - Optional validation function
 * @returns {Object} - Form handling utilities
 * 
 * @example
 * const { values, errors, touched, loading, handleChange, handleBlur, handleSubmit, setFieldValue, resetForm } = useForm({
 *   initialValues: { email: '', password: '' },
 *   onSubmit: async (values) => {
 *     await login(values.email, values.password);
 *   },
 *   validate: (values) => {
 *     const errors = {};
 *     if (!values.email) errors.email = 'Required';
 *     if (!values.password) errors.password = 'Required';
 *     return errors;
 *   }
 * });
 */
const useForm = ({ initialValues, onSubmit, validate }) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Reset form to initial values
  const resetForm = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setLoading(false);
    setSubmitError('');
  }, [initialValues]);

  // Handle input change
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    
    setValues(prevValues => ({
      ...prevValues,
      [name]: fieldValue
    }));
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: undefined
      }));
    }
  }, [errors]);

  // Set a specific field value programmatically
  const setFieldValue = useCallback((name, value) => {
    setValues(prevValues => ({
      ...prevValues,
      [name]: value
    }));
  }, []);

  // Handle input blur (for validation)
  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    
    setTouched(prevTouched => ({
      ...prevTouched,
      [name]: true
    }));
    
    // Validate on blur if validate function is provided
    if (validate) {
      const validationErrors = validate(values);
      setErrors(prevErrors => ({
        ...prevErrors,
        ...validationErrors
      }));
    }
  }, [validate, values]);

  // Handle form submission
  const handleSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();
    
    setSubmitError('');
    
    // Validate all fields if validate function is provided
    let formErrors = {};
    if (validate) {
      formErrors = validate(values);
      setErrors(formErrors);
      
      // Mark all fields as touched
      const touchedFields = Object.keys(values).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      setTouched(touchedFields);
      
      // Don't submit if there are errors
      if (Object.keys(formErrors).length > 0) {
        return;
      }
    }
    
    setLoading(true);
    
    try {
      await onSubmit(values);
    } catch (error) {
      setSubmitError(error.message || 'An error occurred');
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  }, [onSubmit, validate, values]);

  return {
    values,
    errors,
    touched,
    loading,
    submitError,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    resetForm
  };
};

export default useForm;